 import React from 'react';
import { clsx } from 'clsx';

interface TextProps {
  variant?: 'title' | 'subtitle' | 'body' | 'caption' | 'label' | 'note';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  color?: 'primary' | 'secondary' | 'muted' | 'success' | 'warning' | 'error';
  align?: 'left' | 'center' | 'right';
  className?: string;
  children: React.ReactNode;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
}

export const Text: React.FC<TextProps> = ({
  variant = 'body',
  size,
  weight,
  color = 'primary',
  align = 'left',
  className = '',
  children,
  as,
  ...props
}) => {
  // Variant-based defaults
  const variantConfig = {
    title: {
      defaultSize: '2xl',
      defaultWeight: 'bold',
      defaultAs: 'h1' as const,
      margin: 'mb-4',
    },
    subtitle: {
      defaultSize: 'xl',
      defaultWeight: 'semibold',
      defaultAs: 'h2' as const,
      margin: 'mb-3',
    },
    body: {
      defaultSize: 'md',
      defaultWeight: 'normal',
      defaultAs: 'p' as const,
      margin: 'mb-2',
    },
    caption: {
      defaultSize: 'sm',
      defaultWeight: 'normal',
      defaultAs: 'span' as const,
      margin: '',
    },
    label: {
      defaultSize: 'sm',
      defaultWeight: 'medium',
      defaultAs: 'label' as const,
      margin: 'mb-1',
    },
    note: {
      defaultSize: 'xs',
      defaultWeight: 'normal',
      defaultAs: 'p' as const,
      margin: 'mb-1',
    }
  };

  const config = variantConfig[variant];
  
  // Size classes
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
  };

  // Weight classes
  const weightClasses = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  // Color classes (placeholders - replace with your theme colors)
  const colorClasses = {
    primary: 'text-text-primary', // TODO: Replace with actual theme color
    secondary: 'text-text-secondary', // TODO: Replace with actual theme color
    muted: 'text-text-muted', // TODO: Replace with actual theme color
    success: 'text-green-600', // TODO: Replace with actual theme color
    warning: 'text-yellow-600', // TODO: Replace with actual theme color
    error: 'text-red-600', // TODO: Replace with actual theme color
  };

  // Alignment classes
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const Component = as || config.defaultAs;
  const finalSize = (size || config.defaultSize) as keyof typeof sizeClasses;
  const finalWeight = (weight || config.defaultWeight) as keyof typeof weightClasses;

  return (
    <Component
      className={clsx(
        // Size
        sizeClasses[finalSize],
        // Weight
        weightClasses[finalWeight],
        // Color
        colorClasses[color],
        // Alignment
        alignClasses[align],
        // Margin from variant
        config.margin,
        // Line height
        'leading-relaxed',
        // Custom className
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};